import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { trackImageView, recordImageView, checkImageViewPermission } from '../../utils/imageViewTracking';
import ImageViewLimitModal from '../ImageView/ImageViewLimitModal';

const ImageViewer = ({ 
  images = [], 
  girlId, 
  girlName
}) => {
  // ตรวจสอบว่า images เป็น array หรือไม่
  const safeImages = Array.isArray(images) ? images : [];
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [viewTracking, setViewTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(null);

  // โหลดข้อมูลผู้ใช้และสิทธิ์การดูรูป
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData(data);
          
          // ตรวจสอบสิทธิ์การดูรูป
          const today = new Date().toISOString().split('T')[0];
          const todayViews = data.dailyImageViews?.[today] || 0;
          const permission = checkImageViewPermission(data, todayViews);
          setPermissionStatus(permission);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser]);

  // ตรวจสอบและบันทึกการดูรูป
  const handleImageView = async (imageId, imageData) => {
    if (!currentUser || !userData) return;

    try {
      setLoading(true);
      
      // ติดตามการดูรูป
      const tracking = await trackImageView(currentUser.uid, girlId, imageId);
      setViewTracking(tracking);

      // ตรวจสอบสิทธิ์การดูรูป
      const today = new Date().toISOString().split('T')[0];
      const todayViews = userData.dailyImageViews?.[today] || 0;
      const permission = checkImageViewPermission(userData, todayViews);

      if (!permission.canView) {
        setShowLimitModal(true);
        return;
      }

      // บันทึกการดูรูป
      await recordImageView(currentUser.uid, girlId, imageId, imageData);
      
      // อัพเดทข้อมูลผู้ใช้
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const updatedData = userSnap.data();
        setUserData(updatedData);
        
        // อัพเดทสิทธิ์การดูรูป
        const newTodayViews = updatedData.dailyImageViews?.[today] || 0;
        const newPermission = checkImageViewPermission(updatedData, newTodayViews);
        setPermissionStatus(newPermission);
      }

      // แสดงรูปภาพ
      setSelectedImage(imageData);
      setShowImageModal(true);

    } catch (error) {
      console.error('Error handling image view:', error);
      alert('เกิดข้อผิดพลาดในการดูรูปภาพ');
    } finally {
      setLoading(false);
    }
  };

  // ตรวจสอบว่าสามารถดูรูปได้หรือไม่
  const canViewImage = (imageId) => {
    if (!currentUser || !permissionStatus) return false;
    
    // ถ้าเป็นเจ้าของรูป (น้อง) ดูได้เสมอ
    if (currentUser.role === 'girl') return true;
    
    // ถ้าเป็นแอดมิน ดูได้เสมอ
    if (currentUser.role === 'admin') return true;
    
    // ตรวจสอบสิทธิ์การดูรูป
    return permissionStatus.canView;
  };

  // ตรวจสอบว่าต้องซื้อเพื่อดูรูปหรือไม่
  const needsPurchase = (imageId) => {
    if (!currentUser || !permissionStatus) return false;
    
    // ถ้าเป็นเจ้าของรูป (น้อง) ไม่ต้องซื้อ
    if (currentUser.role === 'girl') return false;
    
    // ถ้าเป็นแอดมิน ไม่ต้องซื้อ
    if (currentUser.role === 'admin') return false;
    
    // ตรวจสอบสิทธิ์การดูรูป
    return permissionStatus.needsPurchase;
  };

  // ดูรูปภาพ
  const viewImage = async (image) => {
    if (!currentUser) {
      alert('กรุณาล็อกอินก่อนดูรูป');
      return;
    }

    if (!canViewImage(image.id)) {
      setShowLimitModal(true);
      return;
    }

    if (needsPurchase(image.id)) {
      setShowLimitModal(true);
      return;
    }

    // ใช้ฟังก์ชันใหม่
    await handleImageView(image.id, image);
  };

  // ซื้อดูรูป
  const purchaseImage = async (image) => {
    // TODO: ใช้ระบบ T Coins สำหรับซื้อดูรูป
    alert(`ซื้อดูรูป ${image.originalName || 'รูปภาพ'} เรียบร้อยแล้ว!`);
    
    // หลังจากซื้อแล้ว ให้ดูรูปได้
    setSelectedImage(image);
    setShowImageModal(true);
  };

  // คำนวณจำนวนรูปที่สามารถดูได้
  const getAvailableImages = () => {
    if (currentUser?.role === 'girl' || currentUser?.role === 'admin') {
      return safeImages;
    }

    if (userData?.subscription === 'free') {
      return safeImages.slice(0, 1); // Free Tier ดูได้แค่รูปแรก
    }

    return safeImages;
  };

  const availableImages = getAvailableImages();
  const remainingViews = permissionStatus?.isUnlimited 
    ? 'ไม่จำกัด' 
    : Math.max(0, permissionStatus?.remainingViews || 0);

  return (
    <div className="space-y-4">
      {/* รายการรูปภาพ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {availableImages.map((image, index) => {
          const canView = canViewImage(image.id);
          const needsPurchaseCheck = needsPurchase(image.id);
          const isViewed = false; // TODO: ตรวจสอบจากฐานข้อมูล

          return (
            <div
              key={image.id || index}
              className={`relative group cursor-pointer rounded-lg overflow-hidden ${
                !canView ? 'opacity-50' : ''
              }`}
              onClick={() => viewImage(image)}
            >
              {/* รูปภาพ */}
              <div className="aspect-square bg-gray-100">
                <img
                  src={image.url}
                  alt={`${girlName} - ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDMwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRkZCNkMxIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMjAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiMzMzMiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkltYWdlIEVycm9yPC90ZXh0Pgo8L3N2Zz4K';
                  }}
                />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {loading ? (
                    <div className="text-white text-2xl animate-spin">⏳</div>
                  ) : needsPurchaseCheck ? (
                    <div className="text-white text-center">
                      <div className="text-2xl mb-1">💰</div>
                      <div className="text-xs">ซื้อดู</div>
                    </div>
                  ) : isViewed ? (
                    <div className="text-white text-center">
                      <div className="text-2xl mb-1">👁️</div>
                      <div className="text-xs">ดูแล้ว</div>
                    </div>
                  ) : (
                    <div className="text-white text-center">
                      <div className="text-2xl mb-1">👁️</div>
                      <div className="text-xs">ดูรูป</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Badge */}
              {isViewed && (
                <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                  ดูแล้ว
                </div>
              )}

              {needsPurchaseCheck && (
                <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                  ซื้อดู
                </div>
              )}

              {!canView && (
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                  จำกัด
                </div>
              )}
            </div>
          );
        })}

        {/* รูปที่ต้องซื้อ (สำหรับ Free Tier) */}
        {userData?.subscription === 'free' && safeImages.length > 1 && (
          <div className="aspect-square bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <div className="text-2xl mb-2">🔒</div>
              <div className="text-sm">+{safeImages.length - 1} รูป</div>
              <div className="text-xs">อัพเกรดเพื่อดู</div>
            </div>
          </div>
        )}
      </div>

      {/* Modal ดูรูปภาพ */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">ดูรูปภาพ - {girlName}</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <img
                src={selectedImage.url}
                alt="Preview"
                className="w-full max-h-96 object-contain rounded-lg"
              />
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>ชื่อไฟล์:</strong> {selectedImage.originalName || 'ไม่ระบุ'}
                </div>
                <div>
                  <strong>จำนวนการดู:</strong> {(selectedImage.views || 0) + 1}
                </div>
                <div>
                  <strong>อัพโหลดเมื่อ:</strong> {selectedImage.uploadedAt ? new Date(selectedImage.uploadedAt).toLocaleString('th-TH') : 'ไม่ระบุ'}
                </div>
                <div>
                  <strong>สถานะ:</strong> 
                  <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                    อนุมัติแล้ว
                  </span>
                </div>
              </div>

              {/* ปุ่มซื้อ (ถ้าจำเป็น) */}
              {needsPurchase(selectedImage.id) && (
                <div className="text-center">
                  <button
                    onClick={() => purchaseImage(selectedImage)}
                    className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    ซื้อดูรูปนี้ (10 T Coins)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image View Limit Modal */}
      {showLimitModal && (
        <ImageViewLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          userData={userData}
          currentViewCount={userData?.dailyImageViews?.[new Date().toISOString().split('T')[0]] || 0}
          onUpgradeSuccess={() => {
            // รีเฟรชข้อมูลผู้ใช้
            window.location.reload();
          }}
          onPurchaseSuccess={() => {
            // รีเฟรชข้อมูลผู้ใช้
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default ImageViewer;
